import { useState } from 'react';
import { TrendingDown, X, Pill } from 'lucide-react';
import { reviewCardClass } from './reviewStyles';
import { getMedicationFlagState } from '../../utils/clinicalFlags';
import { getMedicationExtraFields } from '../../utils/drugCategory';
import { useLanguage } from '../../contexts/LanguageContext';

function formatDose(med) {
  if (med.dose_amount != null && med.dose_unit) return `${med.dose_amount}${med.dose_unit}`;
  if (med.dose_amount != null) return String(med.dose_amount);
  if (med.dose) return med.dose;
  if (med.dose_unit) return med.dose_unit;
  return '';
}

function parseDoseInput(value) {
  const trimmed = value.trim();
  if (!trimmed) {
    return { dose_amount: null, dose_unit: null, dose: '' };
  }

  const match = trimmed.match(/^([\d.]+)\s*(.*)$/);
  if (match) {
    const amount = parseFloat(match[1]);
    const unit = match[2].trim() || null;
    return {
      dose_amount: Number.isNaN(amount) ? null : amount,
      dose_unit: unit,
      dose: trimmed,
    };
  }

  return { dose_amount: null, dose_unit: trimmed, dose: trimmed };
}

function handleDoseChange(med, onChange, value) {
  onChange({ ...med, ...parseDoseInput(value) });
}

function getMedDetailPills(med) {
  const dose = formatDose(med);
  const route = med.route || '';
  const time = med.time_given || '';
  return [
    dose ? { key: 'dose', value: dose } : null,
    route ? { key: 'route', value: route } : null,
    time ? { key: 'time', value: time } : null,
  ].filter(Boolean);
}

function capitalizeFirst(text) {
  if (!text) return 'Unknown drug';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function getBpBeforeDraft(med) {
  if (med.bp_systolic_before != null || med.bp_diastolic_before != null) {
    return {
      systolic: med.bp_systolic_before != null ? String(med.bp_systolic_before) : '',
      diastolic: med.bp_diastolic_before != null ? String(med.bp_diastolic_before) : '',
    };
  }
  if (med.bp_before) {
    const parts = String(med.bp_before).split('/');
    return {
      systolic: parts[0]?.trim() || '',
      diastolic: parts[1]?.trim() || '',
    };
  }
  return { systolic: '', diastolic: '' };
}

function buildBpBeforePatch(systolicRaw, diastolicRaw) {
  const sys = String(systolicRaw ?? '').trim();
  const dia = String(diastolicRaw ?? '').trim();
  const sysNum = sys === '' ? null : Number(sys);
  const diaNum = dia === '' ? null : Number(dia);

  const bp_systolic_before = sys === '' || Number.isNaN(sysNum) ? null : sysNum;
  const bp_diastolic_before = dia === '' || Number.isNaN(diaNum) ? null : diaNum;

  return {
    bp_systolic_before,
    bp_diastolic_before,
    bp_before:
      bp_systolic_before != null && bp_diastolic_before != null
        ? `${bp_systolic_before}/${bp_diastolic_before}`
        : null,
  };
}

function BpBeforeInput({ med, onChange, label }) {
  const draft = getBpBeforeDraft(med);

  const handleChange = (field, value) => {
    const next =
      field === 'systolic'
        ? { ...draft, systolic: value }
        : { ...draft, diastolic: value };
    onChange(buildBpBeforePatch(next.systolic, next.diastolic));
  };

  return (
    <div className="block space-y-1">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-gray-500">Systolic</span>
          <input
            type="number"
            inputMode="numeric"
            value={draft.systolic}
            onChange={(e) => handleChange('systolic', e.target.value)}
            placeholder="—"
            className={EXTRA_INPUT_CLASS}
            aria-label="Systolic"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-gray-500">Diastolic</span>
          <input
            type="number"
            inputMode="numeric"
            value={draft.diastolic}
            onChange={(e) => handleChange('diastolic', e.target.value)}
            placeholder="—"
            className={EXTRA_INPUT_CLASS}
            aria-label="Diastolic"
          />
        </label>
      </div>
    </div>
  );
}

const ROUTE_OPTIONS = [
  '',
  'PO',
  'IV',
  'IM',
  'SC',
  'Nebulized',
  'PR',
  'Topical',
  'Other',
];

const EXTRA_INPUT_CLASS =
  'w-full rounded-lg border border-gray-200 px-2 py-1.5 text-base';

const MED_INPUT_CLASS =
  'w-full rounded-lg border border-gray-200 px-2 py-1.5 text-base';

function MedicationExtraFields({ med, onChange, t }) {
  const fields = getMedicationExtraFields(med);
  if (!fields.length) return null;

  const update = (patch) => onChange({ ...med, ...patch });

  return (
    <div className="mt-3 space-y-2 border-t border-gray-100 pt-3" onClick={(e) => e.stopPropagation()}>
      {fields.map((field) => {
        const label = `${t(field.labelKey)}${field.required ? ' *' : ''}`;

        if (field.kind === 'bp' || field.key === 'bp_before') {
          return (
            <BpBeforeInput
              key={field.key}
              med={med}
              label={label}
              onChange={(patch) => update(patch)}
            />
          );
        }

        const isNumeric = [
          'pain_before',
          'pain_after',
          'pulse_before',
          'blood_sugar_before',
          'blood_sugar_after',
          'inr',
        ].includes(field.key);

        return (
          <label key={field.key} className="block space-y-1">
            <span className="text-xs font-medium text-gray-600">{label}</span>
            <input
              type={isNumeric ? 'number' : 'text'}
              inputMode={isNumeric ? 'decimal' : 'text'}
              value={med[field.key] ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (!isNumeric) {
                  update({ [field.key]: raw });
                  return;
                }
                update({ [field.key]: raw === '' ? null : Number(raw) });
              }}
              placeholder={field.required ? t('required') : t('optional')}
              className={EXTRA_INPUT_CLASS}
              min={field.key.startsWith('pain') ? 0 : undefined}
              max={field.key.startsWith('pain') ? 10 : undefined}
            />
          </label>
        );
      })}
    </div>
  );
}

export default function MedicationCard({ med, flags = [], onChange, onDelete, allowDelete = true }) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const isLow = med.confidence === 'low';
  const drugName = med.drug_name || med.name || '';
  const flagState = getMedicationFlagState(flags, drugName);
  const looksUnrecognized = /^\[?\s*unrecognized\b/i.test(String(drugName).trim());
  const displayName =
    (isLow && !confirmed) || looksUnrecognized
      ? 'unrecognized'
      : capitalizeFirst(drugName);

  const detailPills = getMedDetailPills(med);

  const painDelta =
    med.pain_before != null && med.pain_after != null
      ? med.pain_after - med.pain_before
      : null;

  const update = (field, value) => onChange({ ...med, [field]: value });

  const handleConfirm = () => {
    onChange({ ...med, confidence: 'high' });
    setConfirmed(true);
    setExpanded(false);
  };

  return (
    <div
      className={`${reviewCardClass('normal', 'w-full')} ${expanded ? '' : 'cursor-pointer'}`}
      onClick={() => !expanded && setExpanded(true)}
      onKeyDown={(e) => {
        if (!expanded && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          setExpanded(true);
        }
      }}
      role={expanded ? undefined : 'button'}
      tabIndex={expanded ? undefined : 0}
    >
      <div className="flex items-center justify-between gap-2 text-gray-500">
        <div className="flex min-w-0 items-center gap-1.5">
          <Pill className="h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2} />
          <span className="truncate text-xs">Medication</span>
        </div>
        {allowDelete ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
            className="shrink-0 rounded-full p-1 hover:text-primary"
            aria-label="Delete medication"
          >
            <X className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
          <input
            value={drugName}
            onChange={(e) => update('drug_name', e.target.value)}
            placeholder="Drug name"
            className={MED_INPUT_CLASS}
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              inputMode="decimal"
              value={formatDose(med)}
              onChange={(e) => handleDoseChange(med, onChange, e.target.value)}
              placeholder="Dose (e.g. 1g)"
              className={MED_INPUT_CLASS}
            />
            <select
              value={med.route || ''}
              onChange={(e) => update('route', e.target.value)}
              className={MED_INPUT_CLASS}
            >
              {(() => {
                const options = med.route
                  ? ROUTE_OPTIONS.filter((route) => route !== '')
                  : ROUTE_OPTIONS;
                const withCurrent =
                  med.route && !options.includes(med.route)
                    ? [med.route, ...options]
                    : options;
                return withCurrent.map((route) => (
                  <option key={route || 'empty'} value={route}>
                    {route || 'Route'}
                  </option>
                ));
              })()}
            </select>
          </div>
          <input
            type="time"
            value={med.time_given || ''}
            onChange={(e) => update('time_given', e.target.value)}
            placeholder="Time (e.g. 08:00)"
            className={MED_INPUT_CLASS}
          />
          <textarea
            value={med.patient_response || med.response || ''}
            onChange={(e) => update('patient_response', e.target.value)}
            placeholder="Patient response (optional)"
            rows={2}
            className={`${MED_INPUT_CLASS} resize-none`}
          />
          {med.adverse_reaction ? (
            <p className="text-xs font-medium text-amber-700">{t('adverseReactionNoted')}</p>
          ) : null}
          <MedicationExtraFields med={med} onChange={onChange} t={t} />
          {isLow && (
            <button
              type="button"
              onClick={handleConfirm}
              className="w-full rounded-lg bg-success py-2 text-sm font-semibold text-white"
            >
              Confirm
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="w-full rounded-lg bg-gray-100 py-2 text-sm font-medium text-gray-700"
          >
            Done
          </button>
        </div>
      ) : (
        <div className="mt-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[18px] font-bold leading-tight text-gray-900">{displayName}</p>
            {flagState.highAlert && (
              <span className="rounded-full bg-[#FEF2F2] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#DC2626]">
                High alert
              </span>
            )}
            {flagState.verify && (
              <span className="rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#D97706]">
                Verify
              </span>
            )}
          </div>

          {detailPills.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {detailPills.map((pill) => (
                <span
                  key={pill.key}
                  className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                >
                  {pill.value}
                </span>
              ))}
            </div>
          )}

          {(med.patient_response || med.response) && (
            <p className="mt-2 text-xs text-gray-500">{med.patient_response || med.response}</p>
          )}

          {med.adverse_reaction ? (
            <p className="mt-1 text-xs font-medium text-amber-700">{t('adverseReactionNoted')}</p>
          ) : null}

          {med.pain_before != null && med.pain_after != null && (
            <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-gray-600">
              <TrendingDown className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
              <span>
                {med.pain_before}/10 → {med.pain_after}/10
                {painDelta != null && painDelta !== 0 && (
                  <span>
                    {' '}
                    {painDelta < 0 ? '↓' : '↑'}
                    {Math.abs(painDelta)}
                  </span>
                )}
              </span>
            </div>
          )}

          <MedicationExtraFields med={med} onChange={onChange} t={t} />
        </div>
      )}
    </div>
  );
}
